import { Answers, Photo, QuestionAnswerPair, QuestionChange } from "../EditPanel/types";

const DRAFT_TTL = 24 * 60 * 60 * 1000; // 24 hours in ms
const DRAFT_PREFIX = "editor_draft_";

export interface Draft {
  oderId: string;
  versionId: string | number | null; // null = original
  changedAnswers: Answers;
  changedPhotos: Partial<Photo>[]; // only changed fields + photoUrl as identifier
  changedQuestions: QuestionChange[];
  timestamp: number;
  editorId: string;
}

export interface DraftInfo {
  key: string;
  versionId: string | number | null;
  timestamp: number;
  hoursLeft: number;
  changedCount: number;
}

// Generate storage key for a draft
export function getDraftKey(oderId: string, versionId: string | number | null): string {
  const versionPart = versionId || "original";
  return `${DRAFT_PREFIX}${oderId}_${versionPart}`;
}

// Save draft to localStorage
export function saveDraft(
  oderId: string,
  versionId: string | number | null,
  editorId: string,
  changedAnswers: Answers,
  changedPhotos: Partial<Photo>[],
  changedQuestions: QuestionChange[] = []
): void {
  // Don't save empty drafts
  if (Object.keys(changedAnswers).length === 0 && changedPhotos.length === 0 && changedQuestions.length === 0) {
    // If no changes, remove any existing draft
    removeDraft(oderId, versionId);
    return;
  }

  const key = getDraftKey(oderId, versionId);
  const draft: Draft = {
    oderId,
    versionId,
    changedAnswers,
    changedPhotos,
    changedQuestions,
    timestamp: Date.now(),
    editorId,
  };

  try {
    localStorage.setItem(key, JSON.stringify(draft));
  } catch (e) {
    console.error("Failed to save draft:", e);
  }
}

// Load draft from localStorage
export function loadDraft(oderId: string, versionId: string | number | null): Draft | null {
  const key = getDraftKey(oderId, versionId);

  try {
    const data = localStorage.getItem(key);
    if (!data) return null;

    const draft: Draft = JSON.parse(data);

    // Check if expired
    if (Date.now() - draft.timestamp > DRAFT_TTL) {
      localStorage.removeItem(key);
      return null;
    }

    return draft;
  } catch (e) {
    console.error("Failed to load draft:", e);
    return null;
  }
}

// Remove draft from localStorage
export function removeDraft(oderId: string, versionId: string | number | null): void {
  const key = getDraftKey(oderId, versionId);
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error("Failed to remove draft:", e);
  }
}

// Get all drafts for a specific order
export function getAllDraftsForOrder(oderId: string): DraftInfo[] {
  const drafts: DraftInfo[] = [];
  const prefix = `${DRAFT_PREFIX}${oderId}_`;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const data = localStorage.getItem(key);
        if (data) {
          const draft: Draft = JSON.parse(data);
          const age = Date.now() - draft.timestamp;

          // Skip expired drafts and clean them up
          if (age > DRAFT_TTL) {
            localStorage.removeItem(key);
            continue;
          }

          const hoursLeft = Math.max(0, Math.round((DRAFT_TTL - age) / 3600000));
          const changedCount = Object.keys(draft.changedAnswers).length + draft.changedPhotos.length + (draft.changedQuestions?.length || 0);

          drafts.push({
            key,
            versionId: draft.versionId,
            timestamp: draft.timestamp,
            hoursLeft,
            changedCount,
          });
        }
      }
    }
  } catch (e) {
    console.error("Failed to get drafts:", e);
  }

  return drafts;
}

// Calculate changed answers (diff)
export function getChangedAnswers(
  correctedAnswers: Answers,
  baseAnswers: Answers
): Answers {
  const changed: Answers = {};

  for (const question of Object.keys(correctedAnswers)) {
    if (correctedAnswers[question] !== baseAnswers[question]) {
      changed[question] = correctedAnswers[question];
    }
  }

  return changed;
}

// Calculate changed photos (diff)
export function getChangedPhotos(
  currentPhotos: Photo[],
  originalPhotos: Photo[]
): Partial<Photo>[] {
  const changed: Partial<Photo>[] = [];

  for (const current of currentPhotos) {
    const original = originalPhotos.find(p => p.photoUrl === current.photoUrl);
    if (original) {
      const changes: Partial<Photo> = { photoUrl: current.photoUrl };
      let hasChanges = false;

      if (current.description !== original.description) {
        changes.description = current.description;
        hasChanges = true;
      }
      if (current.date !== original.date) {
        changes.date = current.date;
        hasChanges = true;
      }
      if (current.status !== original.status) {
        changes.status = current.status;
        hasChanges = true;
      }
      if (current.hideDate !== original.hideDate) {
        changes.hideDate = current.hideDate;
        hasChanges = true;
      }
      if (current.hideDescription !== original.hideDescription) {
        changes.hideDescription = current.hideDescription;
        hasChanges = true;
      }
      // Смена вопроса у фото = перенос его в другую секцию книги. Без этого
      // PDF-роут не узнаёт о переносе и фото остаётся под старым вопросом.
      if (current.questionToPrecede !== original.questionToPrecede) {
        changes.questionToPrecede = current.questionToPrecede;
        hasChanges = true;
      }
      if (current.isDeleted !== original.isDeleted) {
        changes.isDeleted = current.isDeleted;
        hasChanges = true;
      }

      if (hasChanges) {
        changed.push(changes);
      }
    } else {
      // Фотка добавлена редактором — в original её нет. Кладём в дельту
      // всё поле целиком (с isNew:true), чтобы PDF-роут и applyDraftToPhotos
      // могли её прицепить к pair.photos. isDeleted на new-фото означает,
      // что юзер успел и добавить, и удалить в одной правке — в дельту это
      // тогда не пишем (просто скипаем).
      if (current.isDeleted) continue;
      changed.push({ ...current, isNew: true });
    }
  }

  return changed;
}

// Apply draft changes to answers
export function applyDraftToAnswers(
  baseAnswers: Answers,
  draftChanges: Answers
): Answers {
  return { ...baseAnswers, ...draftChanges };
}

// Apply draft changes to photos
export function applyDraftToPhotos(
  photos: Photo[],
  draftChanges: Partial<Photo>[]
): Photo[] {
  const updated = photos.map(photo => {
    const changes = draftChanges.find(c => c.photoUrl === photo.photoUrl);
    if (changes) {
      return { ...photo, ...changes };
    }
    return photo;
  });
  // Фотки, добавленные в этой версии (isNew), аппендим в конец — у них
  // нет соответствия в base `photos`, поэтому .map() их не подцепит.
  const existingUrls = new Set(photos.map(p => p.photoUrl));
  for (const c of draftChanges) {
    if (c.isNew && c.photoUrl && !existingUrls.has(c.photoUrl)) {
      updated.push(c as Photo);
    }
  }
  return updated;
}

// Calculate changed questions (diff)
export function getChangedQuestions(
  currentPairs: QuestionAnswerPair[],
  originalPairs: QuestionAnswerPair[]
): QuestionChange[] {
  const changed: QuestionChange[] = [];

  for (const current of currentPairs) {
    const original = originalPairs.find(p => p.question === current.question);
    if (!original) continue;

    const change: QuestionChange = { question: current.question };
    let hasChanges = false;

    if (current.clientQuestion !== original.clientQuestion) {
      change.clientQuestion = current.clientQuestion;
      hasChanges = true;
    }
    if (current.isRemoved !== original.isRemoved) {
      change.isRemoved = current.isRemoved;
      hasChanges = true;
    }
    if (current.isQuestionHidden !== original.isQuestionHidden) {
      change.isQuestionHidden = current.isQuestionHidden;
      hasChanges = true;
    }

    if (hasChanges) {
      changed.push(change);
    }
  }

  return changed;
}

// Apply question changes to questionAnswerPairs
export function applyQuestionChanges(
  pairs: QuestionAnswerPair[],
  changes: QuestionChange[]
): QuestionAnswerPair[] {
  return pairs.map(pair => {
    const change = changes.find(c => c.question === pair.question);
    if (change) {
      return {
        ...pair,
        ...(change.clientQuestion !== undefined && { clientQuestion: change.clientQuestion }),
        ...(change.isRemoved !== undefined && { isRemoved: change.isRemoved }),
        ...(change.isQuestionHidden !== undefined && { isQuestionHidden: change.isQuestionHidden }),
      };
    }
    return pair;
  });
}

// Format time ago
export function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин назад`;
  if (hours < 24) return `${hours}ч назад`;
  return "более суток назад";
}
